<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import format from 'date-fns/format';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import OperationsMetricCard from './OperationsMetricCard.vue';
import OperationsLineChart from './OperationsLineChart.vue';
import OperationalConversationsDrawer from './OperationalConversationsDrawer.vue';
import liveReports from 'dashboard/api/liveReports';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();
const metrics = ref({});
const conversationActivity = ref({ data: [] });
const isLoading = ref(true);
const lastUpdatedAt = ref(null);
const selectedTeam = ref(null);
const selectedTimeWindow = ref(15);
const selectedMetric = ref(null);
const [showTeamMenu, toggleTeamMenu] = useToggle();
const [showTimeWindowMenu, toggleTimeWindowMenu] = useToggle();

const allTeams = useMapGetter('teams/getTeams');
const myTeams = useMapGetter('teams/getMyTeams');
const currentRole = useMapGetter('getCurrentRole');
const agentStatus = useMapGetter('agents/getAgentStatus');
const teams = computed(() =>
  currentRole.value === 'supervisor' ? myTeams.value : allTeams.value
);

const teamMenuItems = computed(() => [
  { label: t('OPERATIONS.TEAMS.ALL'), value: null },
  ...teams.value.map(team => ({ label: team.name, value: team.id })),
]);

const selectedTeamLabel = computed(() => {
  return (
    teamMenuItems.value.find(item => item.value === selectedTeam.value)
      ?.label || t('OPERATIONS.TEAMS.ALL')
  );
});

const timeWindowItems = computed(() =>
  [5, 10, 15, 30, 60].map(minutes => ({
    label: t('OPERATIONS.TIME_WINDOWS.OPTION', { minutes }),
    value: minutes,
  }))
);

const selectedTimeWindowLabel = computed(() =>
  t('OPERATIONS.TIME_WINDOWS.SELECTED', { minutes: selectedTimeWindow.value })
);

const metricCards = computed(() => [
  {
    key: 'open',
    label: t('OPERATIONS.METRICS.OPEN'),
    description: t('OPERATIONS.METRICS.OPEN_DESCRIPTION', {
      minutes: selectedTimeWindow.value,
    }),
    icon: 'i-lucide-message-circle',
    tone: 'blue',
  },
  {
    key: 'unattended',
    label: t('OPERATIONS.METRICS.UNATTENDED'),
    description: t('OPERATIONS.METRICS.UNATTENDED_DESCRIPTION', {
      minutes: selectedTimeWindow.value,
    }),
    icon: 'i-lucide-clock-alert',
    tone: 'amber',
  },
  {
    key: 'unassigned',
    label: t('OPERATIONS.METRICS.UNASSIGNED'),
    description: t('OPERATIONS.METRICS.UNASSIGNED_DESCRIPTION', {
      minutes: selectedTimeWindow.value,
    }),
    icon: 'i-lucide-user-round-x',
    tone: 'ruby',
  },
  {
    key: 'pending',
    label: t('OPERATIONS.METRICS.PENDING'),
    description: t('OPERATIONS.METRICS.PENDING_DESCRIPTION', {
      minutes: selectedTimeWindow.value,
    }),
    icon: 'i-lucide-pause-circle',
    tone: 'teal',
  },
]);

const agentStatusCards = computed(() => [
  {
    key: 'online',
    label: t('OPERATIONS.AGENTS.ONLINE'),
    tone: 'teal',
    icon: 'i-lucide-user-round-check',
  },
  {
    key: 'busy',
    label: t('OPERATIONS.AGENTS.BUSY'),
    tone: 'amber',
    icon: 'i-lucide-headset',
  },
  {
    key: 'offline',
    label: t('OPERATIONS.AGENTS.OFFLINE'),
    tone: 'slate',
    icon: 'i-lucide-user-round-x',
  },
]);

const queueChartCollection = computed(() => ({
  categories: metricCards.value.map(card => card.label),
  series: [
    {
      id: 'queue',
      label: t('OPERATIONS.CHARTS.QUEUE.DATASET'),
      color: 'rgb(var(--blue-9))',
      data: metricCards.value.map(card => Number(metrics.value[card.key] || 0)),
    },
  ],
}));

const agentChartCollection = computed(() => ({
  categories: agentStatusCards.value.map(card => card.label),
  series: [
    {
      id: 'agents',
      label: t('OPERATIONS.CHARTS.AGENTS.DATASET'),
      color: 'rgb(var(--teal-9))',
      data: agentStatusCards.value.map(card =>
        Number(agentStatus.value[card.key] || 0)
      ),
    },
  ],
}));

const activityChartCollection = computed(() => ({
  labels: conversationActivity.value.data.map(point =>
    format(new Date(point.timestamp * 1000), 'HH:mm')
  ),
  datasets: [
    {
      label: t('OPERATIONS.CHARTS.ACTIVITY.DATASET'),
      data: conversationActivity.value.data.map(point => point.value),
      borderColor: 'rgb(31, 147, 255)',
      backgroundColor: 'rgba(31, 147, 255, 0.14)',
      fill: true,
      tension: 0.35,
      pointRadius: 2,
      pointHoverRadius: 5,
      pointBackgroundColor: 'rgb(31, 147, 255)',
    },
  ],
}));

const fetchData = async () => {
  if (!metrics.value.open) isLoading.value = true;
  try {
    const params = {
      teamId: selectedTeam.value,
      minutes: selectedTimeWindow.value,
    };
    const [metricsResponse, activityResponse] = await Promise.all([
      liveReports.getConversationMetric(params),
      liveReports.getConversationActivity(params),
    ]);
    metrics.value = metricsResponse.data;
    conversationActivity.value = activityResponse.data;
    lastUpdatedAt.value = new Date();
    await store.dispatch('agents/get');
  } finally {
    isLoading.value = false;
  }
};

const openMetric = card => {
  selectedMetric.value = card;
};

const openMetricFromChart = ({ pointIndex }) => {
  const metric = metricCards.value[pointIndex];
  if (metric) openMetric(metric);
};

const closeDrawer = () => {
  selectedMetric.value = null;
};

const selectTeam = ({ value }) => {
  toggleTeamMenu(false);
  selectedTeam.value = value;
  fetchData();
};

const selectTimeWindow = ({ value }) => {
  toggleTimeWindowMenu(false);
  selectedTimeWindow.value = value;
  fetchData();
};

const { startRefetching } = useLiveRefresh(fetchData, 30000);

onMounted(() => {
  store.dispatch('teams/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="w-full h-full overflow-auto bg-n-surface-1 px-6">
    <div class="max-w-6xl pb-12 mx-auto">
      <ReportHeader
        :header-title="t('OPERATIONS.HEADER')"
        :header-description="t('OPERATIONS.DESCRIPTION')"
      >
        <div class="flex items-center gap-2">
          <span v-if="lastUpdatedAt" class="text-xs text-n-slate-10">
            {{ t('OPERATIONS.UPDATED') }}
          </span>
          <div
            v-on-clickaway="() => toggleTimeWindowMenu(false)"
            class="relative"
          >
            <Button
              sm
              slate
              faded
              icon="i-lucide-clock-3"
              trailing-icon
              :label="selectedTimeWindowLabel"
              @click="toggleTimeWindowMenu()"
            />
            <DropdownMenu
              v-if="showTimeWindowMenu"
              :menu-items="timeWindowItems"
              class="top-full mt-1 ltr:right-0 rtl:left-0"
              @action="selectTimeWindow"
            />
          </div>
          <div v-on-clickaway="() => toggleTeamMenu(false)" class="relative">
            <Button
              sm
              slate
              faded
              trailing-icon
              :label="selectedTeamLabel"
              @click="toggleTeamMenu()"
            />
            <DropdownMenu
              v-if="showTeamMenu"
              :menu-items="teamMenuItems"
              class="top-full mt-1 ltr:right-0 rtl:left-0"
              @action="selectTeam"
            />
          </div>
        </div>
      </ReportHeader>

      <section class="flex flex-col gap-4">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <OperationsMetricCard
            v-for="card in metricCards"
            :key="card.key"
            :label="card.label"
            :value="isLoading ? '—' : metrics[card.key] || 0"
            :description="card.description"
            :icon="card.icon"
            :tone="card.tone"
            @click="openMetric(card)"
          />
        </div>

        <section
          class="rounded-xl bg-n-solid-2 p-5 outline outline-1 outline-n-container"
        >
          <div class="mb-4 flex items-start justify-between gap-4">
            <div class="flex items-start gap-3">
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-blue-3 text-n-blue-11"
              >
                <Icon icon="i-lucide-chart-spline" class="size-5" />
              </span>
              <div>
                <h2 class="mb-1 text-lg font-medium text-n-slate-12">
                  {{ t('OPERATIONS.CHARTS.ACTIVITY.HEADER') }}
                </h2>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    t('OPERATIONS.CHARTS.ACTIVITY.DESCRIPTION', {
                      minutes: selectedTimeWindow,
                    })
                  }}
                </p>
              </div>
            </div>
            <span class="text-xs text-n-slate-10">
              {{ selectedTimeWindowLabel }}
            </span>
          </div>
          <div v-if="!isLoading" class="h-72">
            <OperationsLineChart
              :collection="activityChartCollection"
              :aria-label="t('OPERATIONS.CHARTS.ACTIVITY.HEADER')"
            />
          </div>
          <div
            v-else
            class="flex h-72 items-center justify-center text-sm text-n-slate-10"
          >
            {{ t('OPERATIONS.CHARTS.LOADING') }}
          </div>
        </section>

        <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <section
            class="rounded-xl bg-n-solid-2 p-5 outline outline-1 outline-n-container"
          >
            <div class="mb-4 flex items-start justify-between gap-4">
              <div class="flex items-start gap-3">
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-blue-3 text-n-blue-11"
                >
                  <Icon icon="i-lucide-chart-no-axes-combined" class="size-5" />
                </span>
                <div>
                  <h2 class="mb-1 text-lg font-medium text-n-slate-12">
                    {{ t('OPERATIONS.CHARTS.QUEUE.HEADER') }}
                  </h2>
                  <p class="mb-0 text-sm text-n-slate-10">
                    {{
                      t('OPERATIONS.CHARTS.QUEUE.DESCRIPTION', {
                        minutes: selectedTimeWindow,
                      })
                    }}
                  </p>
                </div>
              </div>
              <span class="flex items-center gap-1.5 text-xs text-n-teal-11">
                <span class="size-1.5 rounded-full bg-n-teal-9" />
                {{ t('OPERATIONS.LIVE') }}
              </span>
            </div>
            <div v-if="!isLoading" class="h-64">
              <BarChart
                :data="queueChartCollection"
                :aria-label="t('OPERATIONS.CHARTS.QUEUE.HEADER')"
                :height="256"
                clickable
                @item-click="openMetricFromChart"
              />
            </div>
            <div
              v-else
              class="flex h-64 items-center justify-center text-sm text-n-slate-10"
            >
              {{ t('OPERATIONS.CHARTS.LOADING') }}
            </div>
            <p class="mt-3 text-xs text-n-slate-10">
              {{ t('OPERATIONS.CHARTS.QUEUE.HINT') }}
            </p>
          </section>

          <section
            class="rounded-xl bg-n-solid-2 p-5 outline outline-1 outline-n-container"
          >
            <div class="mb-4 flex items-start justify-between gap-4">
              <div class="flex items-start gap-3">
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
                >
                  <Icon icon="i-lucide-users-round" class="size-5" />
                </span>
                <div>
                  <h2 class="mb-1 text-lg font-medium text-n-slate-12">
                    {{ t('OPERATIONS.AGENTS.HEADER') }}
                  </h2>
                  <p class="mb-0 text-sm text-n-slate-10">
                    {{ t('OPERATIONS.AGENTS.DESCRIPTION') }}
                  </p>
                </div>
              </div>
              <span class="flex items-center gap-1.5 text-xs text-n-teal-11">
                <span class="size-1.5 rounded-full bg-n-teal-9" />
                {{ t('OPERATIONS.LIVE') }}
              </span>
            </div>
            <div v-if="!isLoading" class="h-64">
              <BarChart
                :data="agentChartCollection"
                :aria-label="t('OPERATIONS.AGENTS.HEADER')"
                :height="256"
              />
            </div>
            <div
              v-else
              class="flex h-64 items-center justify-center text-sm text-n-slate-10"
            >
              {{ t('OPERATIONS.CHARTS.LOADING') }}
            </div>
            <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <OperationsMetricCard
                v-for="card in agentStatusCards"
                :key="card.key"
                :label="card.label"
                :value="agentStatus[card.key] || 0"
                :icon="card.icon"
                :tone="card.tone"
                :clickable="false"
              />
            </div>
          </section>
        </div>
      </section>
    </div>

    <OperationalConversationsDrawer
      :open="!!selectedMetric"
      :metric="selectedMetric?.key"
      :metric-name="selectedMetric?.label"
      :team-id="selectedTeam"
      :minutes="selectedTimeWindow"
      @close="closeDrawer"
    />
  </div>
</template>
